/**
 * Review r37 spec (B1): o nome da forma de pagamento (B4) é o tipo mais a abreviatura e fica gravado SEMPRE em português
 * ("Transferência Itaú"): é a chave da unicidade (índice por nome), o que a migration 0152 derivou, o que os documentos
 * antigos citam e o que o MCP e os importadores casam. A exibição traduz só o rótulo do tipo, no idioma de quem vê
 * ("Bank transfer Itaú", "Transferencia Itaú"); a abreviatura é do cliente e não se traduz.
 */
export const TIPOS_FORMA_PAGAMENTO = ['pix', 'boleto', 'transferencia', 'cartao_credito', 'cartao_debito', 'dinheiro', 'cheque', 'outro'] as const;
export type TipoFormaPagamento = (typeof TIPOS_FORMA_PAGAMENTO)[number];
export type IdiomaForma = 'pt' | 'en' | 'es';

/** Rótulo do tipo nos três idiomas (os mesmos textos de `formas.tipos.*` da tela de Cadastros). */
export const ROTULOS_TIPO_FORMA_IDIOMAS: Record<IdiomaForma, Record<TipoFormaPagamento, string>> = {
  pt: { pix: 'Pix', boleto: 'Boleto', transferencia: 'Transferência', cartao_credito: 'Cartão de crédito', cartao_debito: 'Cartão de débito', dinheiro: 'Dinheiro', cheque: 'Cheque', outro: 'Outro' },
  en: { pix: 'Pix', boleto: 'Bank slip', transferencia: 'Bank transfer', cartao_credito: 'Credit card', cartao_debito: 'Debit card', dinheiro: 'Cash', cheque: 'Check', outro: 'Other' },
  es: { pix: 'Pix', boleto: 'Boleto', transferencia: 'Transferencia', cartao_credito: 'Tarjeta de crédito', cartao_debito: 'Tarjeta de débito', dinheiro: 'Efectivo', cheque: 'Cheque', outro: 'Otro' },
};

const ehTipo = (t: unknown): t is TipoFormaPagamento => typeof t === 'string' && (TIPOS_FORMA_PAGAMENTO as readonly string[]).includes(t);
const idiomaValido = (i: string | null | undefined): IdiomaForma => (i === 'en' || i === 'es' ? i : 'pt');

/** O nome gravado (português): o tipo mais a abreviatura ("Pix Sicredi"); sem abreviatura, só o tipo ("Pix"). */
export function nomeDaFormaPagamento(tipo: TipoFormaPagamento, abreviatura: string | null | undefined): string {
  const a = abreviatura?.trim();
  const r = ROTULOS_TIPO_FORMA_IDIOMAS.pt[tipo];
  return a ? `${r} ${a}` : r;
}

/** Tipos do rótulo mais longo ao mais curto, para "Cartão de crédito X" não casar com um rótulo mais curto. */
const PELO_ROTULO = [...TIPOS_FORMA_PAGAMENTO].sort((a, b) => ROTULOS_TIPO_FORMA_IDIOMAS.pt[b].length - ROTULOS_TIPO_FORMA_IDIOMAS.pt[a].length);

/**
 * Nome da forma para exibir no idioma pedido. Com o tipo e a abreviatura (listas do catálogo), traduz quando o nome
 * gravado é o derivado; o nome que não é derivado (a 0152 manteve alguns) sai como está. Só com o nome (o
 * `forma_pagamento_nome` dos documentos, compras e lançamentos), traduz o rótulo do tipo no começo do nome.
 */
export function rotuloFormaPagamento(f: { nome: string | null | undefined; tipo?: string | null; abreviatura?: string | null }, idioma: string | null | undefined): string {
  const nome = f.nome ?? '';
  const i = idiomaValido(idioma);
  if (i === 'pt' || !nome) return nome;
  if (ehTipo(f.tipo)) {
    if (nome !== nomeDaFormaPagamento(f.tipo, f.abreviatura)) return nome;
    const a = f.abreviatura?.trim();
    return a ? `${ROTULOS_TIPO_FORMA_IDIOMAS[i][f.tipo]} ${a}` : ROTULOS_TIPO_FORMA_IDIOMAS[i][f.tipo];
  }
  for (const t of PELO_ROTULO) {
    const r = ROTULOS_TIPO_FORMA_IDIOMAS.pt[t];
    if (nome === r) return ROTULOS_TIPO_FORMA_IDIOMAS[i][t];
    if (nome.startsWith(`${r} `)) return `${ROTULOS_TIPO_FORMA_IDIOMAS[i][t]}${nome.slice(r.length)}`;
  }
  return nome;
}
